import React, { useState, useEffect } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import ClientTabs from "../Clients/ClientTabs";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import {
  RiWalletLine,
  RiMoneyDollarCircleLine,
  RiTimeLine,
  RiCheckboxCircleLine,
  RiEyeLine,
  RiCloseLine,
  RiMailLine,
  RiPhoneLine,
  RiBuildingLine,
  RiCalendarLine,
  RiCheckDoubleLine,
} from "react-icons/ri";

const ClientWallet = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalPendingRequests: 0,
    todayRequests: 0,
    totalCompletedTransactions: 0,
    totalAmountCredited: 0
  });

  // Pending requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Wallet Modal states
  const [walletShow, setWalletShow] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [amount, setAmount] = useState("");
  const [walletErrors, setWalletErrors] = useState({});
  const [walletBtnLoading, setWalletBtnLoading] = useState(false);

  // Fetch dashboard statistics
  const getDashboardStats = async () => {
    try {
      const response = await apiService.get(config.GETWALLETDASHBOARDSTATS);
      if (response) {
        setDashboardStats(response);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  };

  // Fetch pending requests
  const getPendingRequests = async () => {
    setPendingRequestsLoading(true);
    try {
      const response = await apiService.get(config.GETPENDINGREQUESTS);
      setPendingRequests(response || []);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    } finally {
      setPendingRequestsLoading(false);
    }
  };

  // Approve request
  const handleApproveRequest = async (requestId) => {
    setApprovingId(requestId);
    try {
      const response = await apiService.post(`${config.APPROVEREQUEST}/${requestId}`);
      if (response.success) {
        toast.success("Request approved successfully!");
        await getPendingRequests();
        await getDashboardStats();
        await GetData(pageNumber);
      } else {
        toast.error("Failed to approve request");
      }
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Failed to approve request");
    } finally {
      setApprovingId(null);
    }
  };

  // Get clients data
  const GetData = async (currentPage = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      const apiUrl = `${config.GETCOMPANIES}?companyType=CLIENT&PageNumber=${currentPage}&PageSize=${pageSize}`;
      const response = await apiService.getWithParams(apiUrl);

      const clientsData = response.data || [];
      setClients(clientsData);
      setFilteredClients(clientsData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to fetch clients.");
      setClients([]);
      setFilteredClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      GetData(pageNumber);
      getDashboardStats();
      getPendingRequests();
    }
  }, [user, authLoading, pageNumber]);

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }
    const searchLower = searchValue.toLowerCase();
    const companyMatches = clients
      .filter((client) =>
        client.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);
    setSuggestions(companyMatches);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredClients(clients);
      setShowSuggestions(false);
    } else {
      const filtered = clients.filter((client) =>
        client.companyName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  };

  const handleSuggestionClick = (client) => {
    setSearchTerm(client.companyName);
    setFilteredClients([client]);
    setShowSuggestions(false);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // Pagination functions
  const GetPageData = (number) => {
    if (pageNumber !== number) GetData(number);
  };

  const renderPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).map(
    (number) => (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    )
  );

  // Wallet Modal Functions
  const handleWalletModalOpen = (client) => {
    setSelectedClient(client);
    setWalletShow(true);
  };

  const handleWalletModalClose = () => {
    setWalletShow(false);
    setSelectedClient(null);
    setAmount("");
    setWalletErrors({});
    setWalletBtnLoading(false);
  };

  const validateWalletForm = () => {
    const newErrors = {};
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount greater than 0.";
    }
    setWalletErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveWalletBalance = async () => {
    if (!validateWalletForm()) {
      return;
    }
    setWalletBtnLoading(true);
 const currentDateTime = new Date().toISOString();
    const walletData = {
      CompanyId: selectedClient.companyId,
      Amount: Number(amount),
      Status: "CREDITED",
      PaymentMethod: "ADMIN_CREDIT",
      TransactionNumber: `ADMIN_${Date.now()}`,
      approvedDate: currentDateTime,
    };

    try {
      const response = await apiService.create(config.CREATECOMPANYWALLET, walletData);
      if (response) {

        handleWalletModalClose();
        GetData(pageNumber);
        getDashboardStats();
      } else {
        toast.error("Failed to add balance.");
      }
    } catch (error) {
      console.error("Error adding wallet balance:", error);
      toast.error("An error occurred while adding balance.");
    } finally {
      setWalletBtnLoading(false);
    }
  };

  // View client details
  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowViewModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy hh:mm a");
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <ClientTabs pageName="CLIENTWALLET" />
              </div>

              <div className="card-body">
                {/* Dashboard Stats Cards */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card bg-primary bg-gradient text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">Pending Requests</h6>
                            <h3 className="mb-0">{dashboardStats.totalPendingRequests}</h3>
                          </div>
                          <RiTimeLine size={40} className="opacity-50" />
                        </div>
                        <small className="mt-2 d-block">
                          <RiTimeLine className="me-1" size={12} />
                          {dashboardStats.todayRequests} new today
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-success bg-gradient text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">Completed Transactions</h6>
                            <h3 className="mb-0">{dashboardStats.totalCompletedTransactions}</h3>
                          </div>
                          <RiCheckboxCircleLine size={40} className="opacity-50" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-info bg-gradient text-white">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">Total Amount Credited</h6>
                            <h3 className="mb-0">
                              INR {dashboardStats.totalAmountCredited?.toLocaleString()}
                            </h3>
                          </div>
                          <RiMoneyDollarCircleLine size={40} className="opacity-50" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning bg-gradient text-dark">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">Total Clients</h6>
                            <h3 className="mb-0">{totalRecords}</h3>
                          </div>
                          <RiBuildingLine size={40} className="opacity-50" />
                        </div>
                        <button
                          className="btn btn-sm btn-light mt-2"
                          onClick={() => setShowRequestsModal(true)}
                        >
                          View Pending Requests ({pendingRequests.length})
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div style={{ position: "relative" }}>
                      <i
                        className="ri-search-line"
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                          color: "#6c757d",
                        }}
                      ></i>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by company name..."
                        value={searchTerm}
                        onChange={handleSearch}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={handleInputBlur}
                        style={{
                          height: "45px",
                          paddingLeft: "40px",
                        }}
                      />
                      {showSuggestions && suggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            width: "100%",
                            marginTop: "4px",
                            border: "1px solid #dee2e6",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            zIndex: 1000,
                          }}
                        >
                          {suggestions.map((client) => (
                            <div
                              key={client.companyId}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                              }}
                              onMouseDown={() => handleSuggestionClick(client)}
                            >
                              {client.companyName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="mt-2">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Company Name</th>
                          <th>Contact</th>
                          <th>Wallet Balance</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              Loading clients...
                            </td>
                          </tr>
                        ) : filteredClients.length > 0 ? (
                          filteredClients.map((client, index) => (
                            <tr key={client.companyId}>
                              <td>{(pageNumber - 1) * pageSize + index + 1}</td>
                              <td>
                                <div>
                                  <strong>{client.companyName}</strong>
                                </div>
                                <small className="text-muted">
                                  {client.brandName || "-"}
                                </small>
                              </td>
                              <td>
                                <div>{client.email}</div>
                                <small className="text-muted">
                                  {client.phoneNumber}
                                </small>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {client.currency || 'INR'} {client.walletBalance?.toFixed(2) || "0.00"}
                                </strong>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {client.planType || "N/A"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    client.isActive ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {client.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleWalletModalOpen(client)}
                                >
                                  <i className="ri-add-line me-1"></i>
                                  Add Balance
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No clients found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination section */}
                {filteredClients.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">
                          {((pageNumber - 1) * pageSize) + 1}
                        </span>{" "}
                        to{" "}
                        <span className="fw-semibold">
                          {Math.min(pageNumber * pageSize, totalRecords)}
                        </span>{" "}
                        of <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`} onClick={() => GetPageData(1)}>
                        <Link className="page-link">First</Link>
                      </li>
                      <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`} onClick={() => GetPageData(pageNumber - 1)}>
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers.slice(0, 5)}
                      {totalPages > 5 && (
                        <>
                          <li className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                          {renderPageNumbers.slice(-2)}
                        </>
                      )}
                      <li className={`page-item ${pageNumber === totalPages ? 'disabled' : ''}`} onClick={() => GetPageData(pageNumber + 1)}>
                        <Link className="page-link">Next</Link>
                      </li>
                      <li className={`page-item ${pageNumber === totalPages ? 'disabled' : ''}`} onClick={() => GetPageData(totalPages)}>
                        <Link className="page-link">Last</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add Wallet Balance Modal */}
        <Modal
          show={walletShow}
          onHide={handleWalletModalClose}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Add Wallet Balance for {selectedClient?.companyName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container-fluid">
              <div className="row mb-3">
                <div className="col-12">
                  Current Balance:{" "}
                  <strong className="text-success">
                    {selectedClient?.currency || 'INR'} {selectedClient?.walletBalance?.toFixed(2) || "0.00"}
                  </strong>
                </div>
              </div>
              <div className="row mb-3">
                <label className="col-sm-4 col-form-label">Amount to Add:</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    className={`form-control ${
                      walletErrors.amount ? "is-invalid" : ""
                    }`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    autoFocus
                  />
                  {walletErrors.amount && (
                    <div className="invalid-feedback">
                      {walletErrors.amount}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-end w-100">
              <button
                className="btn btn-danger me-2 px-4"
                onClick={handleWalletModalClose}
              >
                Cancel
              </button>
              {walletBtnLoading ? (
                <button className="btn btn-success px-4" disabled>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </button>
              ) : (
                <button
                  className="btn btn-success px-4"
                  onClick={handleSaveWalletBalance}
                >
                  <i className="fas fa-save me-2"></i>
                  Save
                </button>
              )}
            </div>
          </Modal.Footer>
        </Modal>

       

        {/* Pending Requests Modal - Without Reject Button */}
         <Modal
  show={showRequestsModal}
   onHide={() => setShowRequestsModal(false)}
   size="xl"
   centered
   scrollable
 >
          <Modal.Header closeButton>
            <Modal.Title>
              <RiTimeLine className="me-2" />
              Pending Wallet Requests ({pendingRequests.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {pendingRequestsLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : pendingRequests.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Request Date</th>
                      <th>Company Name</th>
                      <th>Contact Information</th>
                      <th>Payment Method</th>
                      <th>Transaction ID</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((request) => (
                      <tr key={request.companyWalletId}>
                        <td>{formatDate(request.createdAt)}</td>
                        <td>
                          <strong>{request.companyName}</strong>
                         </td>
                        <td>
                          <div>
                            <RiMailLine size={12} className="me-1" />
                            <small>{request.email}</small>
                          </div>
                          <div>
                            <RiPhoneLine size={12} className="me-1" />
                            <small>{request.phoneNumber}</small>
                          </div>
                         </td>
                        <td>
                          <span className="badge bg-info">
                            {request.paymentMethod}
                          </span>
                         </td>
                        <td>
                          <code className="small">{request.transactionNumber}</code>
                         </td>
                        <td>
                          <strong className="text-success">
                            INR {request.amount?.toLocaleString()}
                          </strong>
                         </td>
                        <td>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApproveRequest(request.companyWalletId)}
                            disabled={approvingId === request.companyWalletId}
                            title="Approve Request"
                          >
                            {approvingId === request.companyWalletId ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <RiCheckDoubleLine className="me-1" />
                                Approve
                              </>
                            )}
                          </button>
                         </td>
                       </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4">
                <RiCheckboxCircleLine size={48} className="text-success mb-3" />
                <h5>No Pending Requests</h5>
                <p className="text-muted">All wallet requests have been processed.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-danger"
              onClick={() => setShowRequestsModal(false)}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ClientWallet;